import { memo, useMemo, useCallback } from 'react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { TRAFFIC_HEX, sortByName } from '../shared/constants';
import { useOverviewData } from './useOverviewData';
import { AowCard } from './AowCard';

function IndicatorRow({ item, index }) {
  const aligned = (item.indicators || []).filter(i => !i.isUnaligned);
  const withTarget = aligned.filter(i => i.achievementPct !== null);
  const noTarget = aligned.length - withTarget.length;
  const met = withTarget.filter(i => i.achievementPct >= 100).length;
  const total = withTarget.length;
  const barColor = TRAFFIC_HEX[item.rating] || TRAFFIC_HEX.unknown;
  const barWidth = item.achievementPct != null ? `${Math.min(Math.round(item.achievementPct), 100)}%` : '0%';

  return (
    <div className="py-2.5 border-b border-black/5 last:border-b-0">
      <div className="flex items-start gap-2">
        <span className="text-sm font-bold opacity-40 tabular-nums mt-0.5 w-5 text-right flex-shrink-0">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-800 leading-snug">
            {item.shortTitle || 'Ungrouped'}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <div className="h-1.5 flex-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: barWidth, backgroundColor: barColor }}
              />
            </div>
            <span className="text-[10px] text-gray-500 tabular-nums whitespace-nowrap">
              {total > 0 && <>{met}/{total} indicators met annual target</>}
              {noTarget > 0 && (
                <span className="text-gray-400">{total > 0 ? ' + ' : ''}{noTarget} no target</span>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultBox({ title, items, colors }) {
  return (
    <div
      className="rounded-xl border-2 overflow-hidden flex-1 min-w-0"
      style={{ borderColor: colors.border, backgroundColor: colors.bg }}
    >
      <div className="px-3 py-2" style={{ backgroundColor: colors.headerBg }}>
        <h5 className="text-xs font-bold uppercase tracking-wider" style={{ color: colors.text }}>
          {title}
        </h5>
      </div>
      <div className="px-3 py-1">
        {items.map((item, i) => (
          <IndicatorRow key={item.shortTitle || i} item={item} index={i} />
        ))}
      </div>
    </div>
  );
}

/** Preview wrapper filtered to first AoW (for ChartSelectionModal hover). */
export function AowTocFrameworkPreview() {
  const { areasOfWork } = useOverviewData();
  const first = areasOfWork.find(a => a.hlos?.length);
  if (!first) return null;
  return <AowTocFrameworkDiagram aowFilter={first.id} />;
}

export const AowTocFrameworkDiagram = memo(function AowTocFrameworkDiagram({ aowFilter }) {
  const { areasOfWork: rawAreasOfWork } = useOverviewData();

  const areasOfWork = useMemo(
    () => [...rawAreasOfWork].sort(sortByName),
    [rawAreasOfWork]
  );

  const displayAows = useMemo(
    () => aowFilter ? areasOfWork.filter(a => a.id === aowFilter) : areasOfWork,
    [areasOfWork, aowFilter]
  );

  const chartData = useMemo(() => {
    if (!displayAows.length) return [];
    return displayAows.flatMap(aow =>
      (aow.hlos || []).map(hlo => {
        const aligned = (hlo.indicators || []).filter(i => !i.isUnaligned);
        const withTarget = aligned.filter(i => i.achievementPct !== null);
        return {
          area_of_work: aow.name,
          aow_rating: aow.rating,
          aow_achievement_pct: aow.achievementPct != null ? Math.round(aow.achievementPct) : null,
          result_type: hlo.resultType || 'Untyped',
          short_title: hlo.shortTitle,
          indicator_count: withTarget.length,
          indicators_met_target: withTarget.filter(i => i.achievementPct >= 100).length,
          indicators_no_target: aligned.length - withTarget.length,
          rating: hlo.rating,
        };
      })
    );
  }, [displayAows]);

  const renderBox = useCallback((title, items, colors) => (
    <ResultBox title={title} items={items} colors={colors} />
  ), []);

  if (!displayAows.length) {
    return (
      <ChartWrapper
        title="ToC Framework — Areas of Work"
        info="Displays each Area of Work with its High Level Outputs and Intermediate Outcomes side by side, showing indicator targets and achievements."
        chartData={[]}
        exportFilename="aow-toc-framework"
      >
        <p className="text-sm text-[var(--text-tertiary,#9ca3af)] italic text-center py-8">
          Select a specific program to view the ToC Framework diagram.
        </p>
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper
      title="ToC Framework — Areas of Work"
      info="Displays each Area of Work with its High Level Outputs and Intermediate Outcomes side by side, showing indicator targets and achievements at a glance."
      chartData={chartData}
      exportFilename="aow-toc-framework"
    >
      <div className="space-y-5">
        {displayAows.map(aow => (
          <AowCard key={aow.id} aow={aow} renderBox={renderBox} />
        ))}
      </div>
    </ChartWrapper>
  );
});
